/**
 * Registro centralizado de todos los endpoints del API.
 */
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/Users/Login',
    RENEW_TOKEN: '/Users/RenewToken',
  },
  DYNAMIC_TABLE: {
    GENERATE: '/DynamicTable/generate',
  },
  WIDGET: {
    CONFIG: (widgetId: string) => `/widgets/${widgetId}/config`,
    TABLE_DATA: (widgetId: string) => `/widgets/${widgetId}/table`,
    CHART_DATA: (widgetId: string) => `/widgets/${widgetId}/charts`,
    EXPORT: (widgetId: string) => `/widgets/${widgetId}/export`,
  },
  DATA: {
    QUERY: '/data/query',
    AGGREGATE: '/data/aggregate',
  },
  DASHBOARD_METRICS: {
    /** GET /dashboard-metrics/company/{companyId} — retorna métricas raíz (Id_dashboard=0) de la empresa */
    BY_COMPANY: (companyId: number) => `/dashboard-metrics/company/${companyId}`,
    BY_DASHBOARD: (dashboardId: number) => `/dashboard-metrics/dashboard/${dashboardId}`,
    BY_ID: (id: number) => `/dashboard-metrics/${id}`,
    WITH_DETAILS: (id: number) => `/dashboard-metrics/${id}/with-details`,
    CREATE: '/dashboard-metrics',
    UPDATE: (id: number) => `/dashboard-metrics/${id}`,
    DELETE: (id: number, userId: number) => `/dashboard-metrics/${id}?userId=${userId}`,
    FILTERS: {
      ADD: (metricId: number) => `/dashboard-metrics/${metricId}/filters`,
      UPDATE: (filterId: number) => `/dashboard-metrics/filters/${filterId}`,
      DELETE: (filterId: number) => `/dashboard-metrics/filters/${filterId}`,
    },
    ITEMS: {
      ADD: (metricId: number) => `/dashboard-metrics/${metricId}/items`,
      UPDATE: (itemId: number) => `/dashboard-metrics/items/${itemId}`,
      DELETE: (itemId: number) => `/dashboard-metrics/items/${itemId}`,
    },
  },
  ACTIVITIES: {
    USAGE_RANKING:     '/Activities/usage-ranking',
    GROUPED_BY_MODULE: '/Activities/grouped-by-module',
    BY_ID:             (id: number) => `/Activities/${id}`,
    CREATE:            '/Activities',
    UPDATE:            (id: number) => `/Activities/${id}`,
    DELETE:            (id: number) => `/Activities/${id}`,
    /** Activa o desactiva una actividad, sin tocar el resto de sus campos. */
    UPDATE_ACTIVE_STATUS: (id: number) => `/Activities/${id}/active`,
    /** Busca la actividad legacy (y sus statuses) por nombre exacto dentro de una compañía vieja. */
    LEGACY_STATUS_BY_NAME: (activityName: string, oldCompanyId: number) =>
      `/Activities/legacy-status-by-name?activityName=${encodeURIComponent(activityName)}&oldCompanyId=${oldCompanyId}`,
    /** Clona un formulario (Survey) legacy hacia la actividad nueva: crea/actualiza por nombre+compañía y genera sus steps/status. */
    MIGRATE_SURVEY: (oldActivityId: number, oldCompanyId: number) =>
      `/Activities/migrate-survey/${oldActivityId}?oldCompanyId=${oldCompanyId}`,
  },
  ZONES: {
    BY_COMPANY_USAGE: (companyId: number) => `/Zones/company/${companyId}/by-usage`,
  },
  REPORT_SCHEDULING: {
    TYPES: '/ReportScheduling/types',
    /** Todos los schedules (todos los tipos) configurados por una empresa. */
    LIST_BY_COMPANY: (companyId: number) => `/ReportScheduling/${companyId}`,
    BY_COMPANY_AND_TYPE: (companyId: number, reportType: string) => `/ReportScheduling/${companyId}/${reportType}`,
    TEST_SEND: (companyId: number, reportType: string) => `/ReportScheduling/${companyId}/${reportType}/test-send`,
  },
  ZONE_ACTIVITY_CYCLES: {
    /** GET con ?month=yyyy-MM: ciclo vigente de cada zona. PUT: fija los ciclos desde un mes. */
    BY_COMPANY_AND_ACTIVITY: (companyId: number, activityId: number) => `/ZoneActivityCycles/${companyId}/${activityId}`,
  },
  USERS: {
    FILTERS:         '/Users/filters',
    BY_ID:           (id: number) => `/Users/${id}`,
    CREATE:          '/Users',
    UPDATE:          (id: number) => `/Users/${id}`,
    DELETE:          (id: number) => `/Users/${id}`,
    ACTIVATE:        (id: number) => `/Users/${id}/activate`,
    DEACTIVATE:      (id: number) => `/Users/${id}/deactivate`,
    CREATE_OPERATOR: '/Users/operator',
    CREATE_PORTAL:   '/Users/portal',
    UPDATE_OPERATOR: (id: number) => `/Users/${id}/operator`,
    UPDATE_PORTAL:   (id: number) => `/Users/${id}/portal`,
  },
  USERS_CREDENTIALS: {
    FILTERS: '/Users_credentials/filters',
    BY_ID:   (id: number) => `/Users_credentials/${id}`,
    CREATE:  '/Users_credentials',
    UPDATE:  (id: number) => `/Users_credentials/${id}`,
    DELETE:  (id: number) => `/Users_credentials/${id}`,
  },
  DEVICES: {
    FILTERS: '/Devices/filters',
    BY_ID:   (id: number) => `/Devices/${id}`,
    CREATE:  '/Devices',
    UPDATE:  (id: number) => `/Devices/${id}`,
    DELETE:  (id: number) => `/Devices/${id}`,
    DASHBOARD: '/Dashboards/GetDevicesDashboard',
    DEACTIVATE: (id: number) => `/Devices/${id}/deactivate`,
  },
  OLD_ACTIVITIES: {
    BY_ID:           (id: number)       => `/OldActivities/${id}`,
    STATUS:          (id: number)       => `/OldActivities/${id}/status`,
    SURVEYS_HEADERS: (id: number)       => `/OldActivities/${id}/surveys-headers`,
    SURVEY_DETAILS:  (surveyId: number) => `/OldActivities/surveys/${surveyId}/details`,
    HIERARCHY:       (id: number)       => `/OldActivities/${id}/hierarchy`,
  },
  VISITS: {
    SYNC_COUNTS_BY_MONTH: '/visits/sync-counts-by-month',
    SYNC_COUNTS_BY_DAY:   '/Visits/sync-counts-by-day',
    SYNC_FULL_V3_ASYNC:   '/visits/sync-full-v3-async',
    SYNC_STATUS:          (jobId: string) => `/visits/sync-status/${jobId}`,
    DELETE_BY_PERIOD:     '/Visits/delete-by-period',
  },
  VISITS_BULK: {
    DELETE_BY_PERIOD: '/Visits_bulk/delete-by-period',
  },
  HEADQUARTERS: {
    YEARLY_WEIGHTS: '/Headquarters/yearly-weights',
    WEIGHTS_CREATE: '/Headquarters_weights',
    WEIGHTS_UPDATE: (id: number) => `/Headquarters_weights/${id}`,
  },
  REPORTS: {
    BY_COMPANY: (id: number) => `/Reports/by-company/${id}`,
    CREATE:     '/Reports',
    UPDATE:     (id: number) => `/Reports/${id}`,
    DELETE:     (id: number) => `/Reports/${id}`,
    BY_ID:      (id: number) => `/Reports/${id}`,
    RELOAD:     '/Reports/scheduler/reload',
  },
  IA: {
    ASSISTANT_ASK: (assistantId: string) => `/Ia/assistants/${assistantId}/ask`,
    ASK: '/Ia/ask',
    CHAT_GENERAL: '/Ia/chat/general',
    /**
     * El historial del motor de datos, ya separado por usuario en el servidor.
     *
     * Sólo cubre las preguntas de `ASK`: `CHAT_GENERAL` y los asistentes preconfigurados no
     * guardan nada, así que los modos de ayuda y de pantalla no aparecen aquí.
     */
    CONVERSATIONS: '/Ia/conversations',
    /** Una conversación entera, con sus mensajes y las tablas y gráficos originales. */
    CONVERSATION: (conversationId: string) => `/Ia/conversations/${encodeURIComponent(conversationId)}`,
    /**
     * El historial guardado de verdad: cubre los tres modos del asistente (ayuda, datos,
     * pantalla), con paginado, título, anclado y borrado. No confundir con `CONVERSATIONS`, que
     * es el historial interno del motor de datos y sólo cubre `ASK`.
     */
    THREADS: '/Ia/threads',
    THREAD: (id: number) => `/Ia/threads/${id}`,
    THREAD_MESSAGES: (id: number) => `/Ia/threads/${id}/messages`,
    /**
     * Vacía la caché de resultados de `consultar_metrica` de la empresa del usuario (botón
     * "Refrescar"). El motor de datos la guarda hasta 3 horas; esto trae datos frescos antes.
     */
    REFRESH_METRIC_CACHE: '/Ia/cache/refresh',
  },
  TECHNICAL_DOCS: {
    /** Comprueba si un código de operador tiene permiso ADMINISTRADOR. */
    VALIDATE: '/TechnicalDocs/validate',
    /** Metadatos de las guías. Exige la cabecera X-Access-Code. */
    LIST:     '/TechnicalDocs',
    /** Guía completa con su cuerpo Markdown. Exige la cabecera X-Access-Code. */
    BY_SLUG:  (slug: string) => `/TechnicalDocs/${encodeURIComponent(slug)}`,
  },
  SYNC_TIMES: {
    REPROCESS_TRACKING: (id: number) => `/Sync_times/ReprocessSyncTracking/${id}`,
    STATS_BY_DATE: (date: string, idCompany: number) =>
      `/Sync_times/SyncTrackingStatsByDate?date=${date}&id_company=${idCompany}`,
    REPROCESS_BY_DATE: (date: string, idCompany: number, callbackUrl?: string) =>
      `/Sync_times/ReprocessMissingVisitsByDate?date=${date}&id_company=${idCompany}${
        callbackUrl ? `&callback_url=${encodeURIComponent(callbackUrl)}` : ''
      }`,
  },
} as const;
